import { FunctionComponent, lazy } from 'react';

import Admin from 'pages/Admin';
import Analyses from 'pages/Analyses';
import Analysis from 'pages/Analysis';
import BusinessUnitCharts from 'pages/BusinessUnitCharts';
import BusinessUnitConfig from 'pages/BusinessUnitConfig';
import BusinessUnits from 'pages/BusinessUnits';
import Campaigns from 'pages/Campaigns';
import CarModels from 'pages/CarModels';
import Cars from 'pages/Cars';
import EditCar from 'pages/Cars/EditCar';
import Client from 'pages/Client';
import ClientQuestions from 'pages/ClientQuestions';
import Clients from 'pages/Clients';
import ClientsList from 'pages/ClientsList';
import ConversationEvals from 'pages/ConversationEvals';
import Dashboard from 'pages/Dashboard';
import DbReader from 'pages/DbReader';
import Demochat from 'pages/Demochat';
import NewDemochat from 'pages/Demochat/NewChat';
import ErrorPage from 'pages/Error/ErrorPage';
import Evals from 'pages/Evals';
import FollowUpConfig from 'pages/FollowUpConfig';
import LandingPage from 'pages/LandingPage';
import Leads from 'pages/Leads';
import Onboarding from 'pages/Onboarding';
import PasswordReset from 'pages/PasswordReset';
import Playground from 'pages/Playground';
import PrivacyPolicy from 'pages/PrivacyPolicy';
import Profile from 'pages/Profile';
import PromptSections from 'pages/PromptSections';
import PublicChat from 'pages/PublicChat';
import RealtimeVoice from 'pages/RealtimeVoice';
import Reports from 'pages/Reports';
import SetupOnboarding from 'pages/SetupOnboarding';
import ConfigureLinks from 'pages/SetupOnboarding/ConfigureLinks';
import SignIn from 'pages/SignIn';
import SuccessStories from 'pages/SuccessStories';
import SuccessStoryDetail from 'pages/SuccessStories/SuccessStoryDetail';
import ThemeDemo from 'pages/ThemeDemo';
import Ticket from 'pages/Ticket';
import Tickets from 'pages/Tickets';
import TyC from 'pages/TyC';
import Unauthorized from 'pages/Unauthorized';
import Users from 'pages/Users';
import WhatsAppIntegrationSuccess from 'pages/WhatsAppIntegrationSuccess';
import WhoWeAre from 'pages/WhoWeAre';
import WidgetChat from 'pages/WidgetChat';
import NewWidgetChat from 'pages/WidgetChat/NewChat';
import ZellerAdsConfig from 'pages/ZellerAdsConfig';
import ZellerAdsLeads from 'pages/ZellerAdsLeads';
import ZellerAdsManager from 'pages/ZellerAdsManager';

const Icons = lazy(() => import('pages/Icons'));

export type Role = 'admin' | 'businessUnitAdmin' | 'branchManager' | 'seller' | 'originAdmin';

export type Route = {
  title: string;
  path: string;
  regex?: RegExp;
  component: FunctionComponent;
  private: boolean;
  index: boolean;
  noLayout?: boolean;
  role?: Role[];
};

const routes: { [key: string]: Route } = {
  '/sign-in': {
    title: 'Iniciar Sesión',
    path: '/sign-in',
    component: SignIn,
    private: false,
    index: false,
  },
  '/onboarding': {
    title: 'Registro de Empresa',
    path: '/onboarding',
    component: Onboarding,
    private: false,
    index: false,
  },
  '/dashboard': {
    title: 'Dashboard',
    path: '/dashboard',
    component: Dashboard,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager'],
  },
  '/setup-onboarding': {
    title: 'Configuración Inicial',
    path: '/setup-onboarding',
    component: SetupOnboarding,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin'],
  },
  '/setup-onboarding/configure-links': {
    title: 'Configurar Enlaces de Inventario',
    path: '/setup-onboarding/configure-links',
    component: ConfigureLinks,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin'],
  },
  '/reports': {
    title: 'Reportes',
    path: '/reports',
    component: Reports,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager', 'originAdmin'],
  },
  '/': {
    title: 'Bienvenido',
    path: '/',
    component: LandingPage,
    private: false,
    index: false,
    noLayout: true,
  },
  '/quienes-somos': {
    title: 'Quiénes Somos',
    path: '/quienes-somos',
    component: WhoWeAre,
    private: false,
    index: false,
  },
  '/admin': {
    title: 'Administrador',
    path: '/admin',
    component: Admin,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/clients/:clientId': {
    title: 'Chat WhatsApp',
    path: '/clients/:clientId',
    regex: /\/clients\/\d+/,
    component: Client,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager', 'seller'],
  },
  '/businessUnits': {
    title: 'Empresas',
    path: '/businessUnits',
    component: BusinessUnits,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/businessUnit-follow-up-config/:businessUnitId': {
    title: 'Follow Ups',
    path: '/businessUnit-follow-up-config/:businessUnitId',
    regex: /\/businessUnit-follow-up-config\/\d+/i,
    component: FollowUpConfig,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/businessUnit-config/:businessUnitId': {
    title: 'Empresa Configuraciones',
    path: '/businessUnit-config/:businessUnitId',
    regex: /\/businessUnit-config\/\d+/i,
    component: BusinessUnitConfig,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/businessUnit-charts/:businessUnitId': {
    title: 'Empresa Gráficos',
    path: '/businessUnit-charts/:businessUnitId',
    regex: /\/businessUnit-charts\/\d+/i,
    component: BusinessUnitCharts,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/analysis': {
    title: 'Análisis',
    path: '/analysis',
    component: Analyses,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager'],
  },
  '/analysis/:analysisId': {
    title: 'Análisis',
    path: '/analysis/:analysisId',
    regex: /\/analysis\/\d+/,
    component: Analysis,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager'],
  },
  '/tickets': {
    title: 'Tickets de soporte',
    path: '/tickets',
    component: Tickets,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager', 'seller'],
  },
  '/tickets/:ticketId': {
    title: 'Ticket de soporte',
    path: '/tickets/:ticketId',
    regex: /\/tickets\/\d+/,
    component: Ticket,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager', 'seller'],
  },
  '/cars': {
    title: 'Autos',
    path: '/cars',
    component: Cars,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager'],
  },
  '/cars/:carId/edit': {
    title: 'Editar Auto',
    path: '/cars/:carId/edit',
    regex: /\/cars\/\d+\/edit/,
    component: EditCar,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager'],
  },
  '/carModels': {
    title: 'Modelos de Autos',
    path: '/carModels',
    component: CarModels,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/information': {
    title: 'Información',
    path: '/information',
    component: PromptSections,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin'],
  },
  '/users': {
    title: 'Usuarios',
    path: '/users',
    component: Users,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager'],
  },
  '/clients': {
    title: 'Clientes',
    path: '/clients',
    component: Clients,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager', 'seller'],
  },
  '/clientsList': {
    title: 'Clientes',
    path: '/clientsList',
    component: ClientsList,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager', 'seller'],
  },
  '/icons': {
    title: 'Icons',
    path: '/icons',
    component: Icons,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/unauthorized': {
    title: 'Unauthorized',
    path: '/unauthorized',
    component: Unauthorized,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager', 'originAdmin', 'seller'],
  },
  '/tyc': {
    title: 'Términos y Condiciones',
    path: '/tyc',
    component: TyC,
    private: false,
    index: false,
  },
  '/db-reader': {
    title: 'Analizador de Base de Datos',
    path: '/db-reader',
    component: DbReader,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/playground': {
    title: 'Playground',
    path: '/playground/:messageId',
    regex: /\/playground\/\d+/,
    component: Playground,
    private: true,
    index: false,
    noLayout: true,
    role: ['admin'],
  },
  '/publicChat': {
    title: 'Public Chat',
    path: '/publicChat',
    component: PublicChat,
    private: false,
    index: false,
    noLayout: true,
  },
  '/WhatsAppIntegrationSuccess': {
    title: 'Zeller Integración WhatsApp',
    path: '/WhatsAppIntegrationSuccess',
    component: WhatsAppIntegrationSuccess,
    private: false,
    index: false,
    noLayout: true,
  },
  '/demochat': {
    title: 'Demo Chat',
    path: '/demochat',
    component: ErrorPage,
    private: false,
    index: false,
    noLayout: true,
  },
  '/demochat/:companyIdentifier': {
    title: 'Demo Chat',
    path: '/demochat/:companyIdentifier',
    component: Demochat,
    private: false,
    index: false,
    noLayout: true,
  },
  '/demochat/:companyIdentifier/newchat': {
    title: 'New Demo Chat',
    path: '/demochat/:companyIdentifier/newchat',
    component: NewDemochat,
    private: false,
    index: false,
    noLayout: true,
  },
  '/widgetChat': {
    title: 'Widget Chat',
    path: '/widgetChat',
    component: WidgetChat,
    private: false,
    index: false,
    noLayout: true,
  },
  '/widgetChat/:companyIdentifier': {
    title: 'Widget Chat',
    path: '/widgetChat/:companyIdentifier',
    component: WidgetChat,
    private: false,
    index: false,
    noLayout: true,
  },
  '/widgetChat/:companyIdentifier/newChat': {
    title: 'New Widget Chat',
    path: '/widgetChat/:companyIdentifier/newChat',
    component: NewWidgetChat,
    private: false,
    index: false,
    noLayout: true,
  },
  '/theme': {
    title: 'Theme Demo',
    path: '/theme',
    component: ThemeDemo,
    private: true,
    index: false,
    noLayout: true,
    role: ['admin'],
  },
  '/evals': {
    title: 'Evals',
    path: '/evals',
    component: Evals,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/clientQuestions': {
    title: 'Preguntas Clientes',
    path: '/clientQuestions',
    component: ClientQuestions,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/profile': {
    title: 'Perfil',
    path: '/profile',
    component: Profile,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager', 'originAdmin', 'seller'],
  },
  '/leads': {
    title: 'Leads',
    path: '/leads',
    component: Leads,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager'],
  },
  '/realtimeVoice/:companyIdentifier': {
    title: 'Realtime Voice',
    path: '/realtimeVoice/:companyIdentifier',
    regex: /\/realtimeVoice\/[\w-]+/,
    component: RealtimeVoice,
    private: false,
    index: false,
    noLayout: true,
  },
  '/conversationEvals': {
    title: 'Conversation Evals',
    path: '/conversationEvals',
    component: ConversationEvals,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/campaigns': {
    title: 'Campaigns',
    path: '/campaigns',
    component: Campaigns,
    private: true,
    index: false,
  },
  '/zeller-ads-config': {
    title: 'Configuración de Zeller Ads',
    path: '/zeller-ads-config',
    component: ZellerAdsConfig,
    private: true,
    index: false,
    role: ['admin'],
  },
  '/politica-privacidad-zeller-ads': {
    title: 'Política de Privacidad',
    path: '/politica-privacidad-zeller-ads',
    component: PrivacyPolicy,
    private: false,
    index: false,
  },
  '/zeller-ads-manager': {
    title: 'Zeller Ads Manager',
    path: '/zeller-ads-manager',
    component: ZellerAdsManager,
    private: true,
    index: false,
  },
  '/zeller-ads-leads': {
    title: 'Zeller Ads Leads',
    path: '/zeller-ads-leads',
    component: ZellerAdsLeads,
    private: true,
    index: false,
  },
  '/casos-de-exito': {
    title: 'Casos de éxito',
    path: '/casos-de-exito',
    component: SuccessStories,
    private: false,
    index: false,
  },
  '/casos-de-exito/:storyId': {
    title: 'Caso de éxito',
    path: '/casos-de-exito/:storyId',
    regex: /\/casos-de-exito\/\d+/,
    component: SuccessStoryDetail,
    private: false,
    index: false,
  },
  '/password-reset': {
    title: 'Resetear Contraseña',
    path: '/password-reset',
    component: PasswordReset,
    private: false,
    index: false,
  },
  // this path must be the last one
  error: {
    title: 'Error',
    path: '*',
    component: ErrorPage,
    private: true,
    index: false,
    role: ['admin', 'businessUnitAdmin', 'branchManager', 'originAdmin', 'seller'],
  },
};

export default routes;
